import React, { useEffect } from "react";
import {
  Control,
  useFieldArray,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { Button, Col, FormGroup, Label, Table } from "reactstrap";
import { AiOutlineCopy, AiOutlineDelete } from "react-icons/ai";

import { PackagingItemForm } from "@/@types/ShippingItem";
import { getNewItems } from "@/utils/common-functions";
import classes from "@/styles/get-quotes/get-quotes-main.module.css";

type PackagingItemsProps = {
  reset: UseFormReset<PackagingItemForm>;
  watch: UseFormWatch<PackagingItemForm>;
  control: Control<PackagingItemForm, any>;
  register: UseFormRegister<PackagingItemForm>;
  setValue: UseFormSetValue<PackagingItemForm>;
};

const PackagingItems = ({
  control,
  register,
  watch,
  reset,
  setValue,
}: PackagingItemsProps) => {
  const uom = watch("unitOfMeasurement");
  const { fields, append, insert, remove } = useFieldArray({
    control,
    name: "shippingItems",
  });

  const onAddCopy = (index: number) => {
    // Get the item at the given index
    const itemToCopy = watch("shippingItems")[index];

    // Create a copy of the item
    const newItem = { ...itemToCopy };
    // Append the new item to the array
    insert(index, newItem);
  };

  const handleRowItems = ({ target }: any) => {
    const rowItemCount = parseInt(target.value) - fields.length;

    if (rowItemCount > 0) {
      append(getNewItems(parseInt(target.value) - fields.length));
    } else {
      const slicedItems = watch("shippingItems").slice(0, target.value);

      reset({
        shippingItems: [],
      });

      append(slicedItems);
    }
  };

  useEffect(() => {
    const items = [...watch("shippingItems")];

    for (let i = 0; i < items.length; i++) {
      const element = items[i];
      if (uom === "IMPERIAL") {
        element["weight"] = `${(
          parseFloat(element["weight"]) * 2.2 || 0
        ).toFixed(2)}`;
        element["height"] = `${(
          parseFloat(element["height"] ?? "0") / 2.54 || 0
        ).toFixed(1)}`;
        element["width"] = `${(
          parseFloat(element["width"] ?? "0") / 2.54 || 0
        ).toFixed(1)}`;
        element["length"] = `${(
          parseFloat(element["length"] ?? "0") / 2.54 || 0
        ).toFixed(1)}`;
      }

      if (uom === "METRIC") {
        element["weight"] = `${(
          parseFloat(element["weight"]) / 2.2 || 0
        ).toFixed(2)}`;
        element["height"] = `${(
          parseFloat(element["height"] ?? "0") * 2.54 || 0
        ).toFixed(1)}`;
        element["width"] = `${(
          parseFloat(element["width"] ?? "0") * 2.54 || 0
        ).toFixed(1)}`;
        element["length"] = `${(
          parseFloat(element["length"] ?? "0") * 2.54 || 0
        ).toFixed(1)}`;
      }
    }

    setValue("shippingItems", items);
  }, [setValue, uom, watch]);

  return (
    <>
      <FormGroup row>
        <Label sm={2}>Row of Items</Label>
        <Col sm={2}>
          <select
            className="form-select"
            name="rowOfItems"
            onChange={handleRowItems}
          >
            {new Array(25).fill(0).map((_, i) => (
              <option value={i + 1} key={i}>
                {i + 1}
              </option>
            ))}
          </select>
          <br />
        </Col>
        <Col className="d-flex align-items-center justify-content-end gap-2">
          <p className="m-0 fw-bold">Measurement Unit</p>
          <div className={classes.unitBtnWrapper}>
            <label
              htmlFor="METRIC"
              className={`btn btn-light ${classes.unitBtn} ${
                classes.btnBorderLeft
              } ${uom === "METRIC" && classes.unitBtnActive}`}
            >
              METRIC
              <input
                id="METRIC"
                type="radio"
                {...register("unitOfMeasurement", { required: true })}
                value="METRIC"
                className="d-none"
              />
            </label>
            <label
              htmlFor="IMPERIAL"
              className={`btn btn-light ${classes.unitBtn} ${
                classes.btnBorderRight
              } ${uom === "IMPERIAL" && classes.unitBtnActive}`}
            >
              IMPERIAL
              <input
                id="IMPERIAL"
                type="radio"
                {...register("unitOfMeasurement", { required: true })}
                value="IMPERIAL"
                className="d-none"
              />
            </label>
          </div>
        </Col>
      </FormGroup>
      <Table responsive>
        <thead className={classes.thead}>
          <tr>
            <th>#</th>
            <th className="miw-150px">
              Quantity
              <span className="text-danger">*</span>
            </th>
            <th className="miw-150px">
              Length - {uom === "METRIC" ? "cm(s)" : "in(s)"}{" "}
              <span className="text-danger">*</span>
            </th>
            <th className="miw-150px">
              Width - {uom === "METRIC" ? "cm(s)" : "in(s)"}{" "}
              <span className="text-danger">*</span>
            </th>
            <th className="miw-150px">
              Height - {uom === "METRIC" ? "cm(s)" : "in(s)"}{" "}
              <span className="text-danger">*</span>
            </th>
            <th className="miw-150px">
              Weight - {uom === "METRIC" ? "kg(s)" : "lb(s)"}
              <span className="text-danger">*</span>
            </th>
            <th className="miw-150px">Action</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((item, index) => (
            <tr key={item.id}>
              <td>{index + 1}</td>
              <td>
                <select
                  className="form-select"
                  {...register(`shippingItems.${index}.quantity`, {
                    required: true,
                  })}
                >
                  {new Array(25).fill(0).map((_, i) => (
                    <option value={i + 1} key={i}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  className="form-control"
                  type="text"
                  {...register(`shippingItems.${index}.length`, {
                    required: true,
                  })}
                />
              </td>
              <td>
                <input
                  className="form-control"
                  type="text"
                  {...register(`shippingItems.${index}.width`, {
                    required: true,
                  })}
                />
              </td>
              <td>
                <input
                  className="form-control"
                  type="text"
                  {...register(`shippingItems.${index}.height`, {
                    required: true,
                  })}
                />
              </td>
              <td>
                <input
                  className="form-control"
                  type="text"
                  {...register(`shippingItems.${index}.weight`, {
                    required: true,
                  })}
                />
              </td>
              <td className="d-flex align-items-center gap-1">
                <Button
                  className="btn-darkBlue text-white d-block"
                  type="button"
                  onClick={() => onAddCopy(index)}
                >
                  <AiOutlineCopy />
                </Button>
                <Button
                  className="d-block"
                  type="button"
                  color="danger"
                  onClick={() => remove(index)}
                >
                  <AiOutlineDelete />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <h6>
        Total:{" "}
        {watch("shippingItems")
          .reduce((a, b) => a + parseFloat(b.quantity ?? "1"), 0)
          ?.toFixed(2)}{" "}
        Package | :{" "}
        {(
          watch("shippingItems").reduce(
            (a, b) => a + parseFloat(b.weight) * parseFloat(b.quantity ?? "1"),
            0
          ) || 0
        )?.toFixed(2)}{" "}
        kg
      </h6>
      <Button
        outline
        className="btnDarkBlue-outlined btn-sm float-end"
        onClick={() => {
          append(getNewItems(1));
        }}
      >
        {" "}
        + Add Item
      </Button>
    </>
  );
};

export default PackagingItems;
